import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAdminApi } from '@damar/admin-hooks';
import type {
  Employee,
  CreateEmployeePayload,
  UpdateEmployeePayload,
  Contract,
  CreateContractPayload,
  Department,
  CreateDepartmentPayload,
  Position,
  CreatePositionPayload,
  PayrollPeriod,
  CreatePayrollPeriodPayload,
  Payslip,
  GeneratePayslipPayload,
  Schedule,
  ScheduleEntry,
  ScheduleConflict,
  TenantScheduleConfig,
  CreateSchedulePayload,
  UpdateSchedulePayload,
  CreateScheduleEntryPayload,
  BatchCreateSchedulesPayload,
  BatchCreateSchedulesResult,
  LeaveType,
  CreateLeaveTypePayload,
  UpdateLeaveTypePayload,
  LeavePolicy,
  CreateLeavePolicyPayload,
  UpdateLeavePolicyPayload,
  PublicHoliday,
  CreatePublicHolidayPayload,
  UpdatePublicHolidayPayload,
  LeaveBalanceSummary,
  AdjustBalancePayload,
  LeaveConflict,
  TenantLeaveConfig,
  UpdateTenantLeaveConfigPayload,
  PayrollSupplement,
  CreateSupplementPayload,
  SupplementTemplate,
  CreateSupplementTemplatePayload,
  PreparationReadiness,
  PayrollPreparationPolicy,
  PayrollCalculationOverride,
  DistributePayslipsPayload,
} from './types';

// === Query Keys ===
export const hrKeys = {
  employees: () => ['hr', 'employees'] as const,
  employee: (id: string) => ['hr', 'employees', id] as const,
  contracts: (employeeId?: string | number) => ['hr', 'contracts', employeeId] as const,
  contract: (id: number) => ['hr', 'contracts', 'detail', id] as const,
  departments: () => ['hr', 'departments'] as const,
  positions: () => ['hr', 'positions'] as const,
  payrollPeriods: () => ['hr', 'payrollPeriods'] as const,
  payrollPeriod: (id: string) => ['hr', 'payrollPeriods', id] as const,
  payslipsByPeriod: (periodId: string) => ['hr', 'payslips', 'period', periodId] as const,
  schedules: () => ['hr', 'schedules'] as const,
  schedule: (id: string) => ['hr', 'schedules', id] as const,
  scheduleEntries: (id: string) => ['hr', 'schedules', id, 'entries'] as const,
  scheduleConflicts: (id: string) => ['hr', 'schedules', id, 'conflicts'] as const,
  schedulesByEmployee: (empId: number) => ['hr', 'schedules', 'employee', empId] as const,
  schedulesBySite: (siteId: number) => ['hr', 'schedules', 'site', siteId] as const,
  scheduleConfig: () => ['hr', 'schedules', 'config'] as const,
  leaveTypes: () => ['hr', 'leaveTypes'] as const,
  leavePolicies: () => ['hr', 'leavePolicies'] as const,
  publicHolidays: (year?: number) => ['hr', 'publicHolidays', year] as const,
  leaveBalances: (employeeId: number, year?: number) => ['hr', 'leaveBalances', employeeId, year] as const,
  teamBalances: (managerId: number, year?: number) => ['hr', 'teamBalances', managerId, year] as const,
  tenantLeaveConfig: () => ['hr', 'tenantLeaveConfig'] as const,
  supplements: (periodId: string) => ['hr', 'supplements', periodId] as const,
  supplementTemplates: () => ['hr', 'supplementTemplates'] as const,
  payrollReadiness: (periodId: string) => ['hr', 'payrollReadiness', periodId] as const,
  payrollPolicy: () => ['hr', 'payrollPolicy'] as const,
  payrollOverrides: () => ['hr', 'payrollOverrides'] as const,
};

// === Response mappers ===
// Backend returns flat IDs (employeeId, departmentId, positionId).
// Frontend types expect wrapper objects (employeeKey.id, departmentKey.id, positionKey.id).
// These mappers bridge the gap so all UI components work unchanged.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapEmployee(raw: any): Employee {
  return {
    ...raw,
    // Public UUID — used in routes (/hr/employees/{id}) and audit logs.
    id: raw.id ?? raw.publicId ?? '',
    // Transitional BIGINT — kept for FK joins (timesheets, payroll, leaves)
    // until those modules migrate to UUIDs.
    employeeKey: raw.employeeKey ?? { id: raw.employeeId },
    firstName: raw.personalInfo?.firstName ?? raw.firstName ?? '',
    lastName: raw.personalInfo?.lastName ?? raw.lastName ?? '',
    email: raw.contactInfo?.email ?? raw.email ?? '',
    phone: raw.contactInfo?.phone ?? raw.phone ?? null,
    employeeType: raw.employeeTypeValue ?? raw.employeeType ?? null,
    status: raw.employmentStatusValue ?? raw.status ?? 'ACTIVE',
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapDepartment(raw: any): Department {
  return {
    ...raw,
    departmentKey: raw.departmentKey ?? { id: raw.departmentId },
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapPosition(raw: any): Position {
  return {
    ...raw,
    positionKey: raw.positionKey ?? { id: raw.positionId },
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapContract(raw: any): Contract {
  return {
    ...raw,
    id: raw.id ?? raw.contractId ?? '',
    contractKey: raw.contractKey ?? { id: raw.contractId ?? raw.id },
    contractType: raw.contractTypeValue ?? raw.contractType ?? '',
    contractStatus: raw.contractStatusValue ?? raw.contractStatus ?? '',
    baseSalary: raw.salaryDetails?.baseSalary ?? raw.baseSalary ?? null,
    workingHoursPerWeek: raw.workingConditions?.weeklyHours ?? raw.workingHoursPerWeek ?? null,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapPayrollPeriod(raw: any): PayrollPeriod {
  return {
    ...raw,
    id: raw.id ?? raw.publicId ?? '',
    payrollPeriodKey: raw.payrollPeriodKey ?? { id: raw.payrollPeriodId },
    year: raw.year ?? raw.periodYear,
    month: raw.month ?? raw.periodMonth,
    status: raw.status ?? raw.preparationStatus ?? 'DRAFT',
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapPayslip(raw: any): Payslip {
  return {
    ...raw,
    id: raw.id ?? raw.publicId ?? '',
    payslipKey: raw.payslipKey ?? { id: raw.payslipId },
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapLeaveType(raw: any): LeaveType {
  return {
    ...raw,
    leaveTypeKey: raw.leaveTypeKey ?? { id: raw.leaveTypeId },
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapLeavePolicy(raw: any): LeavePolicy {
  return {
    ...raw,
    leavePolicyKey: raw.leavePolicyKey ?? { id: raw.leavePolicyId },
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapPublicHoliday(raw: any): PublicHoliday {
  return {
    ...raw,
    publicHolidayKey: raw.publicHolidayKey ?? { id: raw.publicHolidayId ?? raw.holidayId },
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapSchedule(raw: any): Schedule {
  return {
    ...raw,
    scheduleKey: raw.scheduleKey ?? { id: raw.scheduleId },
    planningMode: raw.planningModeValue ?? raw.planningMode ?? 'FIXED',
    scheduleStatus: raw.scheduleStatusValue ?? raw.scheduleStatus ?? 'DRAFT',
    periodStartDate: raw.periodStartDate ?? raw.startDate ?? '',
    periodEndDate: raw.periodEndDate ?? raw.endDate ?? '',
    recurrenceConfig: typeof raw.recurrenceConfig === 'string'
      ? JSON.parse(raw.recurrenceConfig)
      : raw.recurrenceConfig ?? {},
  };
}

export function mapScheduleEntry(raw: any): ScheduleEntry {
  return {
    ...raw,
    scheduleEntryKey: raw.scheduleEntryKey ?? { id: raw.scheduleEntryId ?? raw.id },
    totalHours: raw.totalHours ?? 0,
    dayHours: raw.dayHours ?? 0,
    nightHours: raw.nightHours ?? 0,
    breakMinutes: raw.breakMinutes ?? 0,
    isLocked: raw.isLocked ?? raw.locked ?? false,
  };
}

export function mapScheduleConflict(raw: any): ScheduleConflict {
  return {
    ...raw,
    conflictKey: raw.conflictKey ?? { id: raw.scheduleConflictId ?? raw.conflictId ?? raw.id },
    conflictType: raw.conflictType ?? raw.conflictTypeValue ?? '',
    severity: raw.severity ?? raw.severityValue ?? 'LOW',
    conflictStatus: raw.conflictStatus ?? raw.statusValue ?? 'OPEN',
    description: raw.description ?? raw.conflictDescription ?? '',
  };
}

export function mapSupplement(raw: any): PayrollSupplement {
  return {
    ...raw,
    id: raw.id ?? raw.publicId ?? '',
    supplementKey: raw.supplementKey ?? { tenantId: raw.tenantId, publicId: raw.id ?? raw.publicId },
    type: raw.type ?? raw.typeValue ?? '',
    status: raw.status ?? raw.statusValue ?? 'PENDING',
  };
}

// === Employees ===
export function useEmployees() {
  return useQuery({
    queryKey: hrKeys.employees(),
    queryFn: async () => {
      const { data } = await getAdminApi().get<Employee[]>('/api/v1/hr/employees');
      return (data as unknown[]).map(mapEmployee);
    },
  });
}

export function useEmployee(id: string | undefined) {
  return useQuery({
    queryKey: hrKeys.employee(id ?? ''),
    queryFn: async () => {
      const { data } = await getAdminApi().get<Employee>(`/api/v1/hr/employees/${id}`);
      return mapEmployee(data);
    },
    enabled: !!id,
  });
}

export function useCreateEmployee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateEmployeePayload) => {
      const { data } = await getAdminApi().post<Employee>('/api/v1/hr/employees', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hrKeys.employees() });
    },
  });
}

export function useUpdateEmployee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateEmployeePayload }) => {
      const { data } = await getAdminApi().put<Employee>(`/api/v1/hr/employees/${id}`, { ...payload, employeeId: id });
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: hrKeys.employees() });
      queryClient.invalidateQueries({ queryKey: hrKeys.employee(variables.id) });
    },
  });
}

// === Contracts ===
export function useContracts(employeeId?: number) {
  return useQuery({
    queryKey: hrKeys.contracts(employeeId),
    queryFn: async () => {
      const params: Record<string, unknown> = {};
      if (employeeId) params.employeeId = employeeId;
      const { data } = await getAdminApi().get<Contract[]>('/api/v1/hr/contracts', { params });
      return (data as unknown[]).map(mapContract);
    },
  });
}

export function useContract(id: number | undefined) {
  return useQuery({
    queryKey: hrKeys.contract(id ?? 0),
    queryFn: async () => {
      const { data } = await getAdminApi().get<Contract>(`/api/v1/hr/contracts/${id}`);
      return mapContract(data);
    },
    enabled: !!id,
  });
}

export function useCreateContract() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateContractPayload) => {
      const { data } = await getAdminApi().post<Contract>('/api/v1/hr/contracts', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'contracts'] });
      queryClient.invalidateQueries({ queryKey: hrKeys.employees() });
    },
  });
}

export function useUpdateContract() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Partial<CreateContractPayload> }) => {
      const { data } = await getAdminApi().put<Contract>(`/api/v1/hr/contracts/${id}`, payload);
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'contracts'] });
      queryClient.invalidateQueries({ queryKey: hrKeys.contract(variables.id) });
    },
  });
}

// === Departments ===
export function useDepartments() {
  return useQuery({
    queryKey: hrKeys.departments(),
    queryFn: async () => {
      const { data } = await getAdminApi().get<Department[]>('/api/v1/hr/departments');
      return (data as unknown[]).map(mapDepartment);
    },
  });
}

export function useCreateDepartment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateDepartmentPayload) => {
      const { data } = await getAdminApi().post<Department>('/api/v1/hr/departments', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hrKeys.departments() });
    },
  });
}

export function useUpdateDepartment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Partial<CreateDepartmentPayload> }) => {
      const { data } = await getAdminApi().put<Department>(`/api/v1/hr/departments/${id}`, payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hrKeys.departments() });
    },
  });
}

// === Positions ===
export function usePositions() {
  return useQuery({
    queryKey: hrKeys.positions(),
    queryFn: async () => {
      const { data } = await getAdminApi().get<Position[]>('/api/v1/hr/positions');
      return (data as unknown[]).map(mapPosition);
    },
  });
}

export function useCreatePosition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreatePositionPayload) => {
      const { data } = await getAdminApi().post<Position>('/api/v1/hr/positions', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hrKeys.positions() });
    },
  });
}

export function useUpdatePosition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Partial<CreatePositionPayload> }) => {
      const { data } = await getAdminApi().put<Position>(`/api/v1/hr/positions/${id}`, payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hrKeys.positions() });
    },
  });
}

// === Payroll Periods ===
export function usePayrollPeriods() {
  return useQuery({
    queryKey: hrKeys.payrollPeriods(),
    queryFn: async () => {
      const { data } = await getAdminApi().get<PayrollPeriod[]>('/api/v1/hr/payroll/periods');
      return (data as unknown[]).map(mapPayrollPeriod);
    },
  });
}

export function usePayrollPeriod(id: string | undefined) {
  return useQuery({
    queryKey: hrKeys.payrollPeriod(id ?? ''),
    queryFn: async () => {
      const { data } = await getAdminApi().get<PayrollPeriod>(`/api/v1/hr/payroll/periods/${id}`);
      return mapPayrollPeriod(data);
    },
    enabled: !!id,
  });
}

export function useCreatePayrollPeriod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreatePayrollPeriodPayload) => {
      const { data } = await getAdminApi().post<PayrollPeriod>('/api/v1/hr/payroll/periods', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hrKeys.payrollPeriods() });
    },
  });
}

// === Payslips ===
export function usePayslipsByPeriod(periodId: string | undefined) {
  return useQuery({
    queryKey: hrKeys.payslipsByPeriod(periodId ?? ''),
    queryFn: async () => {
      const { data } = await getAdminApi().get<Payslip[]>(`/api/v1/hr/payroll/payslips/period/${periodId}`);
      return (data as unknown[]).map(mapPayslip);
    },
    enabled: !!periodId,
  });
}

export function useGeneratePayslip() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: GeneratePayslipPayload) => {
      const { data } = await getAdminApi().post<Payslip>('/api/v1/hr/payroll/payslips/generate', payload);
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: hrKeys.payslipsByPeriod(variables.payrollPeriodId) });
      queryClient.invalidateQueries({ queryKey: hrKeys.payrollPeriods() });
    },
  });
}

// === Planning / Schedules ===
export function useSchedules() {
  return useQuery({
    queryKey: hrKeys.schedules(),
    queryFn: async () => {
      const { data } = await getAdminApi().get<Schedule[]>('/api/v1/hr/schedules');
      return (data as unknown[]).map(mapSchedule);
    },
  });
}

export function useSchedule(id: string | undefined) {
  return useQuery({
    queryKey: hrKeys.schedule(id ?? ''),
    queryFn: async () => {
      const { data } = await getAdminApi().get<Schedule>(`/api/v1/hr/schedules/${id}`);
      return mapSchedule(data);
    },
    enabled: !!id,
  });
}

export function useSchedulesByEmployee(employeeId: number | undefined) {
  return useQuery({
    queryKey: hrKeys.schedulesByEmployee(employeeId ?? 0),
    queryFn: async () => {
      const { data } = await getAdminApi().get<Schedule[]>(`/api/v1/hr/schedules/employee/${employeeId}`);
      return (data as unknown[]).map(mapSchedule);
    },
    enabled: !!employeeId,
  });
}

export function useSchedulesBySite(siteId: number | undefined) {
  return useQuery({
    queryKey: hrKeys.schedulesBySite(siteId ?? 0),
    queryFn: async () => {
      const { data } = await getAdminApi().get<Schedule[]>(`/api/v1/hr/schedules/site/${siteId}`);
      return (data as unknown[]).map(mapSchedule);
    },
    enabled: !!siteId,
  });
}

export function useScheduleEntries(scheduleId: string | undefined) {
  return useQuery({
    queryKey: hrKeys.scheduleEntries(scheduleId ?? ''),
    queryFn: async () => {
      const { data } = await getAdminApi().get<ScheduleEntry[]>(`/api/v1/hr/schedules/${scheduleId}/entries`);
      return (data as any[]).map(mapScheduleEntry);
    },
    enabled: !!scheduleId,
  });
}

export function useScheduleConflicts(scheduleId: string | undefined) {
  return useQuery({
    queryKey: hrKeys.scheduleConflicts(scheduleId ?? ''),
    queryFn: async () => {
      const { data } = await getAdminApi().get<ScheduleConflict[]>(`/api/v1/hr/schedules/${scheduleId}/conflicts`);
      return (data as any[]).map(mapScheduleConflict);
    },
    enabled: !!scheduleId,
  });
}

export function useScheduleConfig() {
  return useQuery({
    queryKey: hrKeys.scheduleConfig(),
    queryFn: async () => {
      const { data } = await getAdminApi().get<TenantScheduleConfig>('/api/v1/hr/schedules/config');
      return data;
    },
  });
}

export function useCreateSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateSchedulePayload) => {
      const { data } = await getAdminApi().post<Schedule>('/api/v1/hr/schedules', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hrKeys.schedules() });
    },
  });
}

export function useBatchCreateSchedules() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: BatchCreateSchedulesPayload) => {
      const { data } = await getAdminApi().post<BatchCreateSchedulesResult>(
        '/api/v1/hr/schedules/batch-create',
        payload,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hrKeys.schedules() });
    },
  });
}

export function useUpdateSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateSchedulePayload }) => {
      const { data } = await getAdminApi().put<Schedule>(`/api/v1/hr/schedules/${id}`, payload);
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: hrKeys.schedules() });
      queryClient.invalidateQueries({ queryKey: hrKeys.schedule(variables.id) });
    },
  });
}

export function useDeleteSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await getAdminApi().delete(`/api/v1/hr/schedules/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hrKeys.schedules() });
    },
  });
}

export function useCreateScheduleEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ scheduleId, payload }: { scheduleId: string; payload: CreateScheduleEntryPayload }) => {
      const { data } = await getAdminApi().post<ScheduleEntry>(`/api/v1/hr/schedules/${scheduleId}/entries`, payload);
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: hrKeys.scheduleEntries(variables.scheduleId) });
      queryClient.invalidateQueries({ queryKey: hrKeys.scheduleConflicts(variables.scheduleId) });
    },
  });
}

export function useGenerateScheduleEntries() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (scheduleId: string) => {
      const { data } = await getAdminApi().post<ScheduleEntry[]>(`/api/v1/hr/schedules/${scheduleId}/entries/generate`);
      return data;
    },
    onSuccess: (_data, scheduleId) => {
      queryClient.invalidateQueries({ queryKey: hrKeys.scheduleEntries(scheduleId) });
      queryClient.invalidateQueries({ queryKey: hrKeys.scheduleConflicts(scheduleId) });
    },
  });
}

export function useSubmitSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await getAdminApi().post<Schedule>(`/api/v1/hr/schedules/${id}/submit`);
      return data;
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: hrKeys.schedules() });
      queryClient.invalidateQueries({ queryKey: hrKeys.schedule(id) });
    },
  });
}

export function useApproveSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, approvedBy }: { id: string; approvedBy: string }) => {
      const { data } = await getAdminApi().post<Schedule>(
        `/api/v1/hr/schedules/${id}/approve`,
        { scheduleId: id, approvedBy },
      );
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: hrKeys.schedules() });
      queryClient.invalidateQueries({ queryKey: hrKeys.schedule(variables.id) });
    },
  });
}

export function useRejectSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const { data } = await getAdminApi().post<Schedule>(
        `/api/v1/hr/schedules/${id}/reject`,
        { scheduleId: id, reason },
      );
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: hrKeys.schedules() });
      queryClient.invalidateQueries({ queryKey: hrKeys.schedule(variables.id) });
    },
  });
}

export function usePublishSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await getAdminApi().post<Schedule>(`/api/v1/hr/schedules/${id}/publish`);
      return data;
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: hrKeys.schedules() });
      queryClient.invalidateQueries({ queryKey: hrKeys.schedule(id) });
    },
  });
}

// === Leave Types ===
export function useLeaveTypes() {
  return useQuery({
    queryKey: hrKeys.leaveTypes(),
    queryFn: async () => {
      const { data } = await getAdminApi().get<LeaveType[]>('/api/v1/hr/leave-config/types');
      return (data as unknown[]).map(mapLeaveType);
    },
  });
}

export function useCreateLeaveType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateLeaveTypePayload) => {
      const { data } = await getAdminApi().post<LeaveType>('/api/v1/hr/leave-config/types', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hrKeys.leaveTypes() });
    },
  });
}

export function useUpdateLeaveType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: UpdateLeaveTypePayload }) => {
      const { data } = await getAdminApi().put<LeaveType>(`/api/v1/hr/leave-config/types/${id}`, payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hrKeys.leaveTypes() });
    },
  });
}

export function useDeleteLeaveType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await getAdminApi().delete(`/api/v1/hr/leave-config/types/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hrKeys.leaveTypes() });
    },
  });
}

// === Leave Policies ===
export function useLeavePolicies() {
  return useQuery({
    queryKey: hrKeys.leavePolicies(),
    queryFn: async () => {
      const { data } = await getAdminApi().get<LeavePolicy[]>('/api/v1/hr/leave-config/policies');
      return (data as unknown[]).map(mapLeavePolicy);
    },
  });
}

export function useCreateLeavePolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateLeavePolicyPayload) => {
      const { data } = await getAdminApi().post<LeavePolicy>('/api/v1/hr/leave-config/policies', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hrKeys.leavePolicies() });
    },
  });
}

export function useUpdateLeavePolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: UpdateLeavePolicyPayload }) => {
      const { data } = await getAdminApi().put<LeavePolicy>(`/api/v1/hr/leave-config/policies/${id}`, payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hrKeys.leavePolicies() });
    },
  });
}

export function useDeleteLeavePolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await getAdminApi().delete(`/api/v1/hr/leave-config/policies/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hrKeys.leavePolicies() });
    },
  });
}

// === Public Holidays ===
export function usePublicHolidays(year?: number) {
  return useQuery({
    queryKey: hrKeys.publicHolidays(year),
    queryFn: async () => {
      const params: Record<string, unknown> = {};
      if (year !== undefined) params.year = year;
      const { data } = await getAdminApi().get<PublicHoliday[]>('/api/v1/hr/leave-config/holidays', { params });
      return (data as unknown[]).map(mapPublicHoliday);
    },
  });
}

export function useCreatePublicHoliday() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreatePublicHolidayPayload) => {
      const { data } = await getAdminApi().post<PublicHoliday>('/api/v1/hr/leave-config/holidays', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'publicHolidays'] });
    },
  });
}

export function useUpdatePublicHoliday() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: UpdatePublicHolidayPayload }) => {
      const { data } = await getAdminApi().put<PublicHoliday>(`/api/v1/hr/leave-config/holidays/${id}`, payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'publicHolidays'] });
    },
  });
}

export function useDeletePublicHoliday() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await getAdminApi().delete(`/api/v1/hr/leave-config/holidays/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'publicHolidays'] });
    },
  });
}

// === Leave Balances ===
export function useLeaveBalances(employeeId: number | undefined, year?: number) {
  return useQuery({
    queryKey: hrKeys.leaveBalances(employeeId ?? 0, year),
    queryFn: async () => {
      const url = year !== undefined
        ? `/api/v1/hr/leave-balances/employee/${employeeId}/year/${year}`
        : `/api/v1/hr/leave-balances/employee/${employeeId}`;
      const { data } = await getAdminApi().get<LeaveBalanceSummary[]>(url);
      return data;
    },
    enabled: !!employeeId,
  });
}

export function useAdjustBalance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: AdjustBalancePayload) => {
      const { data } = await getAdminApi().post('/api/v1/hr/leave-balances/adjust', payload);
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'leaveBalances', variables.employeeId] });
    },
  });
}

// === Tenant Leave Config ===
export function useTenantLeaveConfig() {
  return useQuery({
    queryKey: hrKeys.tenantLeaveConfig(),
    queryFn: async () => {
      const { data } = await getAdminApi().get<TenantLeaveConfig>('/api/v1/hr/leave-config/tenant-config');
      return data;
    },
  });
}

export function useUpdateTenantLeaveConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateTenantLeaveConfigPayload) => {
      const { data } = await getAdminApi().put<TenantLeaveConfig>('/api/v1/hr/leave-config/tenant-config', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hrKeys.tenantLeaveConfig() });
    },
  });
}

// === Team Calendar ===
export function useTeamCalendar(employeeIds: number[], month: number, year: number) {
  return useQuery({
    queryKey: ['hr', 'teamCalendar', employeeIds, month, year],
    queryFn: async () => {
      const { data } = await getAdminApi().get('/api/v1/hr/leaves/calendar', {
        params: { employeeIds: employeeIds.join(','), month, year },
      });
      return data;
    },
    enabled: employeeIds.length > 0,
  });
}

// === Leave Conflicts ===
export function useLeaveConflicts(empId: number | undefined, start: string, end: string) {
  return useQuery({
    queryKey: ['hr', 'leaveConflicts', empId, start, end],
    queryFn: async () => {
      const { data } = await getAdminApi().get<LeaveConflict[]>('/api/v1/hr/leaves/conflicts', {
        params: { employeeId: empId, startDate: start, endDate: end },
      });
      return data;
    },
    enabled: !!empId && !!start && !!end,
  });
}

// === Cancel Leave ===
export function useCancelLeave() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await getAdminApi().post(`/api/v1/hr/leaves/${id}/cancel`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'leaves'] });
      queryClient.invalidateQueries({ queryKey: ['my', 'leaves'] });
    },
  });
}

// === Payroll Supplements ===
export function usePayrollSupplements(periodId: string | undefined) {
  return useQuery({
    queryKey: hrKeys.supplements(periodId ?? ''),
    queryFn: async () => {
      const { data } = await getAdminApi().get<PayrollSupplement[]>(`/api/v1/hr/payroll/supplements?periodId=${periodId}`);
      return (data as any[]).map(mapSupplement);
    },
    enabled: !!periodId,
  });
}

export function useCreateSupplement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateSupplementPayload) => {
      const { data } = await getAdminApi().post<PayrollSupplement>('/api/v1/hr/payroll/supplements', payload);
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: hrKeys.supplements(variables.payrollPeriodId) });
    },
  });
}

export function useApproveSupplement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ supplementId, approvedBy }: { supplementId: string; approvedBy: number }) => {
      const { data } = await getAdminApi().post(`/api/v1/hr/payroll/supplements/${supplementId}/approve`, { supplementId, approvedBy });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'supplements'] });
    },
  });
}

export function useRejectSupplement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (supplementId: string) => {
      const { data } = await getAdminApi().post(`/api/v1/hr/payroll/supplements/${supplementId}/reject`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'supplements'] });
    },
  });
}

// === Supplement Templates ===
export function useSupplementTemplates() {
  return useQuery({
    queryKey: hrKeys.supplementTemplates(),
    queryFn: async () => {
      const { data } = await getAdminApi().get<SupplementTemplate[]>('/api/v1/hr/payroll/supplement-templates');
      return data;
    },
  });
}

export function useCreateSupplementTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateSupplementTemplatePayload) => {
      const { data } = await getAdminApi().post<SupplementTemplate>('/api/v1/hr/payroll/supplement-templates', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hrKeys.supplementTemplates() });
    },
  });
}

// === Preparation & Readiness ===
export function usePreparePayrollPeriod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (periodId: string) => {
      const { data } = await getAdminApi().post<PreparationReadiness>(`/api/v1/hr/payroll/periods/${periodId}/prepare`);
      return data;
    },
    onSuccess: (_data, periodId) => {
      queryClient.invalidateQueries({ queryKey: hrKeys.payrollReadiness(periodId) });
      queryClient.invalidateQueries({ queryKey: hrKeys.payrollPeriod(periodId) });
    },
  });
}

export function usePayrollReadiness(periodId: string | undefined) {
  return useQuery({
    queryKey: hrKeys.payrollReadiness(periodId ?? ''),
    queryFn: async () => {
      const { data } = await getAdminApi().get<PreparationReadiness>(`/api/v1/hr/payroll/periods/${periodId}/readiness`);
      return data;
    },
    enabled: !!periodId,
  });
}

export function usePayrollPeriodSummary(periodId: string | undefined) {
  return useQuery({
    queryKey: ['hr', 'payroll', 'period-summary', periodId ?? ''],
    queryFn: async () => {
      const { data } = await getAdminApi().get<import('./types').PayrollPeriodSummary>(
        `/api/v1/hr/payroll/periods/${periodId}/summary`,
      );
      return data;
    },
    enabled: !!periodId,
  });
}

// === Batch Generation ===
export function useGenerateBatchPayslips() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (periodId: string) => {
      const { data } = await getAdminApi().post(`/api/v1/hr/payroll/periods/${periodId}/generate`);
      return data;
    },
    onSuccess: (_data, periodId) => {
      queryClient.invalidateQueries({ queryKey: hrKeys.payslipsByPeriod(periodId) });
      queryClient.invalidateQueries({ queryKey: hrKeys.payrollPeriod(periodId) });
    },
  });
}

// === Distribution ===
export function useDistributePayslips() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: DistributePayslipsPayload) => {
      const { data } = await getAdminApi().post(`/api/v1/hr/payroll/periods/${payload.payrollPeriodId}/distribute`, payload);
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: hrKeys.payrollPeriod(variables.payrollPeriodId) });
    },
  });
}

// === Reopen ===
export function useReopenPayrollPeriod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ periodId, reason }: { periodId: string; reason?: string }) => {
      const { data } = await getAdminApi().post(`/api/v1/hr/payroll/periods/${periodId}/reopen`, { payrollPeriodId: periodId, reason });
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: hrKeys.payrollPeriod(variables.periodId) });
    },
  });
}

// === Policy ===
export function usePayrollPolicy() {
  return useQuery({
    queryKey: hrKeys.payrollPolicy(),
    queryFn: async () => {
      const { data } = await getAdminApi().get<PayrollPreparationPolicy>('/api/v1/hr/payroll/config/policy');
      return data;
    },
  });
}

export function useUpdatePayrollPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<PayrollPreparationPolicy>) => {
      const { data } = await getAdminApi().put('/api/v1/hr/payroll/config/policy', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hrKeys.payrollPolicy() });
    },
  });
}

// === Overrides ===
export function usePayrollOverrides() {
  return useQuery({
    queryKey: hrKeys.payrollOverrides(),
    queryFn: async () => {
      const { data } = await getAdminApi().get<PayrollCalculationOverride[]>('/api/v1/hr/payroll/config/overrides');
      return data;
    },
  });
}

export async function downloadPayslipPdf(payslipId: string, filename: string): Promise<void> {
  const response = await getAdminApi().get(`/api/v1/hr/payroll/payslips/${payslipId}/pdf`, {
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export async function downloadDeclaration(
  year: number,
  quarter: number,
  type: 'CSS' | 'IPRES',
  format: 'csv' | 'pdf',
): Promise<void> {
  const response = await getAdminApi().get(
    `/api/v1/hr/payroll/declarations/${year}/${quarter}`,
    { params: { type, format }, responseType: 'blob' },
  );
  const ext = format === 'pdf' ? 'pdf' : 'csv';
  const filename = `declaration_${type}_${year}_T${quarter}.${ext}`;
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// ---- Timesheet Import ----

export interface ImportTemplate {
  templateId: number;
  name: string;
  description?: string;
  fileFormat: string;
  defaultStatus: string;
}

export interface ImportPreviewLine {
  row: number;
  employeeCode: string;
  employeeName: string | null;
  regularHours: number;
  overtimeHours: number;
  totalHoursWorked: number | null;
  status: 'VALID' | 'ERROR';
  errors: string[];
}

export interface ImportPreviewResult {
  previewId: string;
  templateName: string;
  defaultStatus: string;
  year: number;
  month: number;
  totalRows: number;
  validRows: number;
  errorRows: number;
  lines: ImportPreviewLine[];
}

export function useImportTemplates() {
  return useQuery({
    queryKey: ['hr', 'timesheet-import-templates'],
    queryFn: async () => {
      const { data } = await getAdminApi().get<ImportTemplate[]>('/api/v1/hr/timesheets/import/templates');
      return data;
    },
  });
}

export async function uploadTimesheetPreview(
  file: File, templateId: number, year: number, month: number,
): Promise<ImportPreviewResult> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('templateId', String(templateId));
  formData.append('year', String(year));
  formData.append('month', String(month));
  const { data } = await getAdminApi().post<ImportPreviewResult>(
    '/api/v1/hr/timesheets/import/preview', formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

export async function confirmTimesheetImport(previewId: string): Promise<{ inserted: number; skipped: number; status: string }> {
  const { data } = await getAdminApi().post(`/api/v1/hr/timesheets/import/confirm/${previewId}`);
  return data;
}
